import * as React from 'react';
import ReactSVG from 'react-inlinesvg';
import { TooltipTriggerProps } from 'react-popper-tooltip';

import { useTheme } from '@emotion/react';

import { Theme, styled } from '../theme';
import { Size, matchSize } from '../utility/common-props-helpers';
import Tooltip from './Tooltip';

const getDimension = matchSize({
    medium: 12,
    large: 16,
    huge: 24,
    huge2: 50,
    huge3: 115,
});

const TOOLTIP_OFFSET_X = 10;
const tooltipModifiers = [{ name: 'offset', options: { offset: [0, TOOLTIP_OFFSET_X] } }];

const StyledSvgIcon = styled('div')<{
    size: Size;
    width?: string | number;
    height?: string | number;
    color?: string;
}>(({ theme, size, width, height, color }) => ({
    display: 'inline-block',
    verticalAlign: 'middle',
    cursor: 'pointer',
    width: width != null ? width : getDimension(size),
    height: height != null ? height : getDimension(size),
    color: color || 'inherit',
    svg: {
        display: 'block',
        fill: 'currentColor',
        color: color || 'inherit',
        width: width != null ? width : getDimension(size),
        height: height != null ? height : getDimension(size),
    },
}));

export type SvgIconProps = {
    className?: string;
    style?: React.CSSProperties;
    size?: Size;
    width?: number | string;
    height?: number | string;
    src: string;
    alt?: string;
    onClick?: (ev) => void;
    onMouseDown?: (ev) => void;
    color?: (theme: Theme) => string;
    tooltip?: string;
    hasTooltipOffsetX?: boolean;
};

/**
 * Component
 *
 * @example
 * ```
 * <SvgIcon alt="github" src="/icons/ic-github.svg" color={theme => theme.color.green1} />
 * <SvgIcon alt="github" src={require('/icons/ic-github.svg')} />
 * ```
 */
const SvgIcon = React.forwardRef(
    (
        {
            src,
            alt,
            size = 'medium',
            color,
            tooltip,
            hasTooltipOffsetX,
            onClick,
            ...props
        }: SvgIconProps,
        ref
    ) => {
        const theme = useTheme();
        const handleClick = (ev) => {
            onClick && onClick(ev);
        };

        const icon = (
            <StyledSvgIcon
                title={alt}
                size={size}
                ref={ref}
                color={color && color(theme)}
                onClick={handleClick}
                {...props}
            >
                <ReactSVG src={src} style={{ display: 'flex' }} cacheRequests={true} />
            </StyledSvgIcon>
        );

        const withTooltip = !!tooltip;

        if (!withTooltip) {
            return icon;
        }
        const tooltipProps: Partial<TooltipTriggerProps> = {};
        if (hasTooltipOffsetX) {
            tooltipProps.modifiers = tooltipModifiers;
        }

        return (
            <Tooltip title={tooltip} disableFocusListener {...tooltipProps}>
                {icon}
            </Tooltip>
        );
    }
);

export default SvgIcon;
